// 단체 통화 피어 매시(Mesh) 관리자 — 참가자별 RTCPeerConnection 묶음 관리
// - 1:1 통화 파일 무변경 원칙: supabase 직접 참조 및 채널 구독/해제 일절 없음
// - 신호 송신은 엔진이 주입한 send(toUserId, type, data) 함수를 통해서만 수행
import { removeGroupRemoteAudio } from './groupCallMedia';

const MAX_EARLY_ICE_PER_PEER = 50;
const ICE_BATCH_DELAY_MS = 100;
const RECONNECT_INTERVAL_MS = 5000;
const RECONNECT_TIMEOUT_MS = 60000;
const RECONNECT_IN_FLIGHT_MS = 8000;

function isHostTcpCandidate(candidateStr) {
  if (typeof candidateStr !== 'string') return false;
  return candidateStr.includes(' tcp ') && candidateStr.includes('typ host');
}

export function createMesh({
  myUserId,
  callId: _callId,
  localStream,
  iceServers,
  send,
  onPeerAudio,
  onPeerState,
  onPeerGone,
  checkPeerAlive,
  onRemoteVideo,
  onPeerConnected,
}) {
  let currentLocalVideoTrack = null;
  let currentQualityLevel = 0;
  // peerId -> { pc, makingOffer: boolean, pendingIce: Array, outIce: Array, outIceTimer: any }
  const peers = new Map();
  // peerId -> Array of candidates (아직 pc가 생성되기 전에 도착한 후보)
  const earlyIceMap = new Map();
  // peerId -> { startedAt: number, attempts: number, lastAttemptAt: number, skipCount: number, retryTimer: any, deadlineTimer: any }
  const reconnectingMap = new Map();
  // peerId -> 마지막 reconnect-please 처리 시각(ms)
  const lastReconnectPleaseAtMap = new Map();
  // peerId -> 마지막 reconnect: true offer 수신 시각(ms)
  const lastReconnectOfferAtMap = new Map();
  // peerId -> 내가(작은 id) 마지막으로 보낸 재연결 offer 번호
  const localGenMap = new Map();
  // peerId -> 내가(큰 id) 받아들인 가장 큰 재연결 offer 번호
  const remoteGenMap = new Map();

  function isGen(v) {
    return Number.isInteger(v) && v > 0;
  }

  function isSmallerId(peerId) {
    return String(myUserId).toLowerCase() < String(peerId).toLowerCase();
  }

  function getInFlightMs(peerId) {
    const recon = reconnectingMap.get(peerId);
    if (!recon) return -1;
    const pc = peers.get(peerId)?.pc;
    if (!pc) return -1;
    const state = pc.connectionState;
    if (state === 'failed' || state === 'closed' || state === 'connected') return -1;
    const elapsed = Date.now() - (recon.lastAttemptAt || 0);
    if (elapsed < RECONNECT_IN_FLIGHT_MS) {
      return elapsed;
    }
    return -1;
  }

  function clearReconnectEntry(peerId) {
    const recon = reconnectingMap.get(peerId);
    if (!recon) return;
    if (recon.retryTimer) {
      clearTimeout(recon.retryTimer);
      recon.retryTimer = null;
    }
    if (recon.deadlineTimer) {
      clearTimeout(recon.deadlineTimer);
      recon.deadlineTimer = null;
    }
    reconnectingMap.delete(peerId);
  }

  function resetPeerPc(peerId) {
    const peerEntry = peers.get(peerId);
    if (!peerEntry) return;
    if (peerEntry.outIceTimer) {
      clearTimeout(peerEntry.outIceTimer);
      peerEntry.outIceTimer = null;
    }
    peerEntry.outIce = [];
    peerEntry.pendingIce = [];
    try {
      peerEntry.pc.onconnectionstatechange = null;
      peerEntry.pc.ontrack = null;
      peerEntry.pc.onicecandidate = null;
      peerEntry.pc.close();
    } catch (e) {
      console.warn(`[mesh] peer ${peerId?.slice?.(0, 8)} pc.close 실패:`, e);
    }
    peers.delete(peerId);
  }

  function scheduleReconnectRetry(peerId) {
    const recon = reconnectingMap.get(peerId);
    if (!recon) return;
    if (recon.retryTimer) {
      clearTimeout(recon.retryTimer);
    }
    recon.retryTimer = setTimeout(async () => {
      const currentRecon = reconnectingMap.get(peerId);
      if (!currentRecon) return;
      currentRecon.retryTimer = null;
      const currentEntry = peers.get(peerId);
      if (currentEntry?.pc?.connectionState === 'connected') {
        clearReconnectEntry(peerId);
        return;
      }

      if (typeof checkPeerAlive === 'function') {
        try {
          const aliveRes = await checkPeerAlive(peerId);
          if (!reconnectingMap.has(peerId)) return;
          if (peers.get(peerId)?.pc?.connectionState === 'connected') {
            clearReconnectEntry(peerId);
            return;
          }
          if (aliveRes && !aliveRes.defer && aliveRes.alive === false) {
            console.log(`[단체 재연결] 중단 — 상대: ${peerId?.slice?.(0, 8)}, 사유: 서버 명단에 없음`);
            clearReconnectEntry(peerId);
            closePeer(peerId);
            onPeerGone?.(peerId, 'left');
            return;
          }
        } catch (err) {
          console.warn(`[mesh] peer ${peerId?.slice?.(0, 8)} 생존 확인 실패:`, err);
        }
      }

      if (!reconnectingMap.has(peerId) || currentRecon.retryTimer !== null) return;
      const latestEntry = peers.get(peerId);
      if (latestEntry?.pc?.connectionState === 'connected') {
        clearReconnectEntry(peerId);
        return;
      }

      if (isSmallerId(peerId)) {
        const inFlightMs = getInFlightMs(peerId);
        if (inFlightMs >= 0) {
          console.log(
            `[단체 재연결] 회차 건너뜀 — 상대: ${peerId?.slice?.(0, 8)}, 사유: 걸기 진행 중(${Math.round(inFlightMs / 1000)}초)`,
          );
          scheduleReconnectRetry(peerId);
          return;
        }
        if (latestEntry?.pc?.connectionState === 'connecting' && currentRecon.skipCount < 2) {
          currentRecon.skipCount += 1;
          console.log(`[단체 재연결] 회차 건너뜀 — 상대: ${peerId?.slice?.(0, 8)}, 사유: 연결 진행 중`);
          scheduleReconnectRetry(peerId);
          return;
        }
        currentRecon.skipCount = 0;
        currentRecon.attempts += 1;
        currentRecon.lastAttemptAt = Date.now();
        console.log(`[단체 재연결] 재시도 ${currentRecon.attempts}회 — 상대: ${peerId?.slice?.(0, 8)}`);
        scheduleReconnectRetry(peerId);
        resetPeerPc(peerId);
        setupPeerConnection(peerId);
        onPeerState?.(peerId, 'connecting');
        void connectTo(peerId, { reconnect: true });
      } else {
        currentRecon.attempts += 1;
        currentRecon.lastAttemptAt = Date.now();
        scheduleReconnectRetry(peerId);
        if (Date.now() - (lastReconnectOfferAtMap.get(peerId) || 0) < RECONNECT_IN_FLIGHT_MS) {
          console.log(`[단체 재연결] 다시 걸기 요청 보류 — 상대: ${peerId?.slice?.(0, 8)}, 사유: 상대가 거는 중`);
        } else {
          console.log(`[단체 재연결] 다시 걸기 요청 보냄 — 상대: ${peerId?.slice?.(0, 8)}`);
          void send(peerId, 'reconnect-please', { trigger: 'timer' }).catch((err) => {
            console.warn(`[mesh] peer ${peerId?.slice?.(0, 8)} reconnect-please 전송 실패:`, err);
          });
        }
      }
    }, RECONNECT_INTERVAL_MS);
  }

  function reconnectPeer(peerId, reason = 'failed') {
    if (!peerId || peerId === myUserId) return;
    const isMyIdSmaller = isSmallerId(peerId);
    const existingRecon = reconnectingMap.get(peerId);

    if (existingRecon) {
      if (Date.now() - existingRecon.lastAttemptAt < 1000 && reason === 'failed') {
        return;
      }
      existingRecon.skipCount = 0;
      existingRecon.attempts += 1;
      existingRecon.lastAttemptAt = Date.now();
      scheduleReconnectRetry(peerId);
      resetPeerPc(peerId);
      setupPeerConnection(peerId);
      onPeerState?.(peerId, 'connecting');
      if (isMyIdSmaller) {
        console.log(`[단체 재연결] 재시도 ${existingRecon.attempts}회 — 상대: ${peerId?.slice?.(0, 8)}`);
        void connectTo(peerId, { reconnect: true });
      } else if (reason === 'visible') {
        console.log(`[단체 재연결] 다시 걸기 요청 보냄 — 상대: ${peerId?.slice?.(0, 8)}`);
        void send(peerId, 'reconnect-please', { trigger: 'visible' }).catch((err) => {
          console.warn(`[mesh] peer ${peerId?.slice?.(0, 8)} reconnect-please 전송 실패:`, err);
        });
      }
      return;
    }

    console.log(`[단체 재연결] 시작 — 상대: ${peerId?.slice?.(0, 8)}, 계기: ${reason}`);
    const recon = {
      startedAt: Date.now(),
      attempts: 0,
      lastAttemptAt: Date.now(),
      skipCount: 0,
      retryTimer: null,
      deadlineTimer: null,
    };
    recon.deadlineTimer = setTimeout(() => {
      if (!reconnectingMap.has(peerId)) return;
      clearReconnectEntry(peerId);
      console.warn(`[단체 재연결] 포기 — 상대: ${peerId?.slice?.(0, 8)}, 사유: 60초 초과`);
      closePeer(peerId);
      onPeerGone?.(peerId, 'failed');
    }, RECONNECT_TIMEOUT_MS);

    reconnectingMap.set(peerId, recon);
    scheduleReconnectRetry(peerId);
    resetPeerPc(peerId);
    setupPeerConnection(peerId);
    onPeerState?.(peerId, 'connecting');

    if (isMyIdSmaller) {
      void connectTo(peerId, { reconnect: true });
    } else if (reason === 'visible') {
      console.log(`[단체 재연결] 다시 걸기 요청 보냄 — 상대: ${peerId?.slice?.(0, 8)}`);
      void send(peerId, 'reconnect-please', { trigger: 'visible' }).catch((err) => {
        console.warn(`[mesh] peer ${peerId?.slice?.(0, 8)} reconnect-please 전송 실패:`, err);
      });
    }
  }

  function storeEarlyIce(peerId, cand, gen) {
    if (!cand) return;
    let list = earlyIceMap.get(peerId);
    if (!list) {
      list = [];
      earlyIceMap.set(peerId, list);
    }
    if (list.length >= MAX_EARLY_ICE_PER_PEER) {
      list.shift(); // 오래된 것 버림
    }
    list.push({ candidate: cand, gen: isGen(gen) ? gen : undefined });
  }

  function queueOutIce(peerEntry, peerId, cand) {
    peerEntry.outIce.push(cand);
    if (!peerEntry.outIceTimer) {
      peerEntry.outIceTimer = setTimeout(() => {
        peerEntry.outIceTimer = null;
        if (peerEntry.outIce.length === 0) return;
        const batch = peerEntry.outIce;
        peerEntry.outIce = [];
        const payload = isGen(peerEntry.gen)
          ? { candidates: batch, gen: peerEntry.gen }
          : { candidates: batch };
        void send(peerId, 'ice', payload).catch((err) => {
          console.warn(`[mesh] peer ${peerId?.slice?.(0, 8)} ICE 전송 실패:`, err);
        });
      }, ICE_BATCH_DELAY_MS);
    }
  }

  function setupPeerConnection(peerId) {
    const pc = new RTCPeerConnection({ iceServers });
    const peerEntry = {
      pc,
      makingOffer: false,
      pendingIce: [],
      outIce: [],
      outIceTimer: null,
    };
    peers.set(peerId, peerEntry);

    // 로컬 오디오 트랙 추가
    if (localStream) {
      localStream.getAudioTracks().forEach((track) => {
        try {
          pc.addTrack(track, localStream);
        } catch (e) {
          console.warn(`[mesh] peer ${peerId?.slice?.(0, 8)} 트랙 추가 실패:`, e);
        }
      });
    }

    pc.ontrack = (event) => {
      if (peers.get(peerId)?.pc !== pc) return;
      if (event.track && event.track.kind === 'video') {
        console.log(`[단체 화상] 상대 영상 수신 — 상대: ${peerId}`);
        onRemoteVideo?.(peerId, new MediaStream([event.track]));
        return;
      }
      if (event.streams && event.streams[0]) {
        onPeerAudio?.(peerId, event.streams[0]);
      }
    };

    pc.onicecandidate = (event) => {
      if (peers.get(peerId)?.pc !== pc) return;
      if (!event.candidate) return;
      const candStr = event.candidate.candidate || '';
      if (isHostTcpCandidate(candStr)) {
        // host tcp 제외 (1:1 규칙과 동일)
        return;
      }
      const candJson = event.candidate.toJSON ? event.candidate.toJSON() : event.candidate;
      queueOutIce(peerEntry, peerId, candJson);
    };

    pc.onconnectionstatechange = () => {
      if (peers.get(peerId)?.pc !== pc) return;
      const state = pc.connectionState;
      if (state === 'connected') {
        const recon = reconnectingMap.get(peerId);
        if (recon) {
          const elapsedSec = Math.round((Date.now() - recon.startedAt) / 1000);
          clearReconnectEntry(peerId);
          console.log(`[단체 재연결] 성공 — 상대: ${peerId?.slice?.(0, 8)}, 걸린 시간: ${elapsedSec}초`);
        }
        try {
          onPeerConnected?.(peerId);
        } catch {}
      }
      onPeerState?.(peerId, state);
      if (state === 'failed') {
        if (!reconnectingMap.has(peerId)) {
          reconnectPeer(peerId, 'failed');
        }
      }
    };

    return peerEntry;
  }

  async function applyStoredIce(peerEntry, peerId) {
    const pc = peerEntry.pc;
    const early = earlyIceMap.get(peerId) || [];
    earlyIceMap.delete(peerId);
    const pending = peerEntry.pendingIce;
    peerEntry.pendingIce = [];

    let discardedCount = 0;
    for (const item of early) {
      if (!item) continue;
      const cand = item.candidate !== undefined ? item.candidate : item;
      const candGen = item.gen;
      if (isGen(candGen) && isGen(peerEntry.gen) && candGen !== peerEntry.gen) {
        discardedCount += 1;
        continue;
      }
      if (!cand) continue;
      try {
        await pc.addIceCandidate(new RTCIceCandidate(cand));
      } catch (e) {
        console.warn(`[mesh] peer ${peerId?.slice?.(0, 8)} 보관 ICE 적용 실패:`, e);
      }
    }
    if (discardedCount > 0) {
      console.log(`[단체 재연결] 남은 옛 접속 정보 버림 — 상대: ${peerId?.slice?.(0, 8)}, 개수: ${discardedCount}`);
    }

    for (const cand of pending) {
      if (!cand) continue;
      try {
        await pc.addIceCandidate(new RTCIceCandidate(cand));
      } catch (e) {
        console.warn(`[mesh] peer ${peerId?.slice?.(0, 8)} 보관 ICE 적용 실패:`, e);
      }
    }
  }

  async function connectTo(peerId, options = {}) {
    if (!peerId || peerId === myUserId) return;
    let peerEntry = peers.get(peerId);
    if (!peerEntry) {
      peerEntry = setupPeerConnection(peerId);
    }
    const pc = peerEntry.pc;
    if (!peerEntry.videoSender) {
      try {
        const vt = pc.addTransceiver('video', { direction: 'sendrecv' });
        peerEntry.videoSender = vt.sender;
        if (currentQualityLevel > 0) void applyVideoQuality(vt.sender, currentQualityLevel);
        console.log(`[단체 화상] 영상 칸 준비 — 상대: ${peerId}, 역할: 거는 쪽`);
        if (currentLocalVideoTrack && currentLocalVideoTrack.readyState === 'live') {
          if (vt.sender.track !== currentLocalVideoTrack) {
            vt.sender.replaceTrack(currentLocalVideoTrack).catch(() => {});
            const timing = options?.reconnect === true || reconnectingMap.has(peerId) ? '재연결' : '보내는 쪽';
            console.log(`[단체 화상] 연결 시 내 카메라 붙임 — 상대: ${peerId}, 시점: ${timing}`);
          }
        }
      } catch (err) {
        console.warn(`[mesh] peer ${peerId?.slice?.(0, 8)} video transceiver 추가 실패:`, err);
      }
    }
    try {
      peerEntry.makingOffer = true;
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      if (peers.get(peerId)?.pc !== pc) return;
      const isReconnect = options?.reconnect === true || reconnectingMap.has(peerId);
      let payload;
      if (isReconnect) {
        const gen = (localGenMap.get(peerId) || 0) + 1;
        localGenMap.set(peerId, gen);
        peerEntry.gen = gen;
        payload = { sdp: pc.localDescription, reconnect: true, gen };
      } else {
        payload = { sdp: pc.localDescription };
      }
      await send(peerId, 'offer', payload);
    } catch (err) {
      console.warn(`[mesh] peer ${peerId?.slice?.(0, 8)} connectTo(offer) 실패:`, err);
    } finally {
      peerEntry.makingOffer = false;
    }
  }

  async function handleOffer(peerId, sdp, isReconnect = false, gen) {
    if (!peerId || !sdp) return;
    let peerEntry = peers.get(peerId);
    const isMyIdSmaller = isSmallerId(peerId);

    if (!isReconnect) {
      remoteGenMap.delete(peerId);
      localGenMap.delete(peerId);
    }

    if (isReconnect && !isMyIdSmaller && isGen(gen)) {
      const known = remoteGenMap.get(peerId) || 0;
      if (gen < known) {
        console.log(`[단체 재연결] 옛 걸기 무시 — 상대: ${peerId?.slice?.(0, 8)}, 번호: ${gen}, 최신: ${known}`);
        return;
      }
      if (gen === known && peerEntry && peerEntry.gen === gen) {
        console.log(`[단체 재연결] 같은 걸기 중복 무시 — 상대: ${peerId?.slice?.(0, 8)}, 번호: ${gen}`);
        return;
      }
      remoteGenMap.set(peerId, gen);
    }

    if (isReconnect && !isMyIdSmaller) {
      lastReconnectOfferAtMap.set(peerId, Date.now());
    }

    if (isReconnect && !isMyIdSmaller && peerEntry) {
      // 큰 쪽이 reconnect: true가 붙은 offer를 받으면, 내 pc가 옛 세대(화면 꺼짐 전 연결)일 수 있으므로 pc를 새로 만든 뒤 응답한다.
      resetPeerPc(peerId);
      peerEntry = null;
    } else if (peerEntry && peerEntry.pc.signalingState === 'have-local-offer') {
      if (isMyIdSmaller) {
        // 내 id가 더 작으면 받은 offer 무시 (내 offer 유지)
        console.log(`[mesh] peer ${peerId?.slice?.(0, 8)} 동시 offer 충돌 — 내 id 우선, 받은 offer 무시`);
        return;
      }
      // 상대 id가 더 작으면 내 pc 닫고 새 pc로 응답
      console.log(`[mesh] peer ${peerId?.slice?.(0, 8)} 동시 offer 충돌 — 상대 id 우선, 내 pc 재생성 후 응답`);
      resetPeerPc(peerId);
      peerEntry = null;
    } else if (peerEntry && peerEntry.pc.remoteDescription) {
      // 기존 연결이 있던 상대가 재연결 새 offer를 보낸 경우 기존 pc를 닫고 새 pc로 수락
      resetPeerPc(peerId);
      peerEntry = null;
    }

    if (!peerEntry) {
      peerEntry = setupPeerConnection(peerId);
    }
    if (isGen(gen)) {
      peerEntry.gen = gen;
    }
    const pc = peerEntry.pc;

    if (reconnectingMap.has(peerId)) {
      scheduleReconnectRetry(peerId);
    }

    try {
      await pc.setRemoteDescription(new RTCSessionDescription(sdp));
      await applyStoredIce(peerEntry, peerId);
      const videoTransceiver = pc.getTransceivers().find(
        (t) => t.receiver?.track?.kind === 'video' && t.direction !== 'stopped'
      );
      if (videoTransceiver) {
        videoTransceiver.direction = 'sendrecv';
        peerEntry.videoSender = videoTransceiver.sender;
        if (currentQualityLevel > 0) void applyVideoQuality(videoTransceiver.sender, currentQualityLevel);
        console.log(`[단체 화상] 영상 칸 준비 — 상대: ${peerId}, 역할: 받는 쪽`);
        if (currentLocalVideoTrack && currentLocalVideoTrack.readyState === 'live') {
          if (videoTransceiver.sender.track !== currentLocalVideoTrack) {
            videoTransceiver.sender.replaceTrack(currentLocalVideoTrack).catch(() => {});
            const timing = isReconnect ? '재연결' : '받는 쪽';
            console.log(`[단체 화상] 연결 시 내 카메라 붙임 — 상대: ${peerId}, 시점: ${timing}`);
          }
        }
      } else {
        console.log(`[단체 화상] 영상 칸 없음 — 상대: ${peerId}`);
      }
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      const payload = isGen(peerEntry.gen)
        ? { sdp: pc.localDescription, gen: peerEntry.gen }
        : { sdp: pc.localDescription };
      await send(peerId, 'answer', payload);
    } catch (err) {
      console.warn(`[mesh] peer ${peerId?.slice?.(0, 8)} handleOffer 실패:`, err);
    }
  }

  async function handleAnswer(peerId, sdp, gen) {
    if (!peerId || !sdp) return;
    const peerEntry = peers.get(peerId);
    if (!peerEntry) return;
    const pc = peerEntry.pc;

    if (isGen(gen) && peerEntry.gen !== gen) {
      console.log(
        `[단체 재연결] 옛 답장 무시 — 상대: ${peerId?.slice?.(0, 8)}, 번호: ${gen}, 현재: ${peerEntry.gen ?? '없음'}`,
      );
      return;
    }

    // pc가 'have-local-offer' 일 때만 answer 적용
    if (pc.signalingState !== 'have-local-offer') {
      console.warn(`[mesh] peer ${peerId?.slice?.(0, 8)} answer 수신 무시 — signalingState: ${pc.signalingState}`);
      return;
    }

    if (reconnectingMap.has(peerId)) {
      scheduleReconnectRetry(peerId);
    }

    try {
      await pc.setRemoteDescription(new RTCSessionDescription(sdp));
      await applyStoredIce(peerEntry, peerId);
    } catch (err) {
      console.warn(`[mesh] peer ${peerId?.slice?.(0, 8)} handleAnswer 실패:`, err);
    }
  }

  async function handleIce(peerId, candidates, gen) {
    if (!peerId || !Array.isArray(candidates)) return;
    const peerEntry = peers.get(peerId);

    if (peerEntry && isGen(gen) && isGen(peerEntry.gen) && gen < peerEntry.gen) {
      console.log(
        `[단체 재연결] 옛 접속 정보 무시 — 상대: ${peerId?.slice?.(0, 8)}, 번호: ${gen}, 현재: ${peerEntry.gen}, 개수: ${candidates.length}`,
      );
      return;
    }

    if (peerEntry && isGen(gen) && isGen(peerEntry.gen) && gen > peerEntry.gen) {
      for (const cand of candidates) {
        storeEarlyIce(peerId, cand, gen);
      }
      return;
    }

    const pc = peerEntry?.pc;
    if (pc && pc.remoteDescription && pc.remoteDescription.type) {
      for (const cand of candidates) {
        if (!cand) continue;
        try {
          await pc.addIceCandidate(new RTCIceCandidate(cand));
        } catch (e) {
          console.warn(`[mesh] peer ${peerId?.slice?.(0, 8)} 즉시 ICE 적용 실패:`, e);
        }
      }
    } else if (peerEntry) {
      for (const cand of candidates) {
        if (!cand) continue;
        if (peerEntry.pendingIce.length >= MAX_EARLY_ICE_PER_PEER) {
          peerEntry.pendingIce.shift();
        }
        peerEntry.pendingIce.push(cand);
      }
    } else {
      for (const cand of candidates) {
        storeEarlyIce(peerId, cand, gen);
      }
    }
  }

  function handleReconnectPlease(peerId, data) {
    if (!peerId || peerId === myUserId) return;
    const trigger = data?.trigger === 'timer' ? 'timer' : 'visible';
    if (!isSmallerId(peerId)) {
      console.log(`[단체 재연결] 다시 걸기 요청 받음 — 상대: ${peerId?.slice?.(0, 8)}, 처리: 무시(내가 큰 id)`);
      return;
    }
    const now = Date.now();
    const lastAt = lastReconnectPleaseAtMap.get(peerId) || 0;
    if (now - lastAt < 2000) {
      console.log(`[단체 재연결] 다시 걸기 요청 받음 — 상대: ${peerId?.slice?.(0, 8)}, 처리: 무시(2초 내 중복)`);
      return;
    }
    const peerEntry = peers.get(peerId);
    const isReconnecting = reconnectingMap.has(peerId);
    const connState = peerEntry?.pc?.connectionState || 'closed';
    const iceState = peerEntry?.pc?.iceConnectionState || 'closed';
    const isConnected = Boolean(
      peerEntry &&
      !isReconnecting &&
      connState === 'connected' &&
      iceState !== 'disconnected' &&
      iceState !== 'failed' &&
      iceState !== 'closed'
    );
    if (isConnected) {
      console.log(`[단체 재연결] 다시 걸기 요청 받음 — 상대: ${peerId?.slice?.(0, 8)}, 처리: 무시(이미 연결됨)`);
      return;
    }
    if (trigger === 'timer' && getInFlightMs(peerId) >= 0) {
      console.log(`[단체 재연결] 다시 걸기 요청 받음 — 상대: ${peerId?.slice?.(0, 8)}, 처리: 무시(걸기 진행 중)`);
      return;
    }
    lastReconnectPleaseAtMap.set(peerId, now);
    console.log(`[단체 재연결] 다시 걸기 요청 받음 — 상대: ${peerId?.slice?.(0, 8)}, 처리: 즉시 걸기, 계기: ${trigger}`);
    reconnectPeer(peerId, 'reconnect-please');
  }

  function closePeer(peerId) {
    clearReconnectEntry(peerId);
    lastReconnectPleaseAtMap.delete(peerId);
    lastReconnectOfferAtMap.delete(peerId);
    localGenMap.delete(peerId);
    remoteGenMap.delete(peerId);
    resetPeerPc(peerId);
    earlyIceMap.delete(peerId);
    removeGroupRemoteAudio(peerId);
    try {
      onRemoteVideo?.(peerId, null);
    } catch {}
  }

  function closeAll() {
    const reconIds = Array.from(reconnectingMap.keys());
    for (const peerId of reconIds) {
      clearReconnectEntry(peerId);
    }
    const ids = Array.from(peers.keys());
    for (const peerId of ids) {
      closePeer(peerId);
    }
    currentQualityLevel = 0;
    earlyIceMap.clear();
    lastReconnectPleaseAtMap.clear();
    lastReconnectOfferAtMap.clear();
    localGenMap.clear();
    remoteGenMap.clear();
  }

  function peerIds() {
    return Array.from(peers.keys());
  }

  function hasPeer(peerId) {
    return peers.has(peerId);
  }

  function getReconnectingPeerIds() {
    return Array.from(reconnectingMap.keys());
  }

  function getPeerStates() {
    const list = [];
    peers.forEach((entry, id) => {
      const pc = entry.pc;
      const connState = pc?.connectionState || 'closed';
      const iceState = pc?.iceConnectionState || 'closed';
      const effectiveState =
        reconnectingMap.has(id)
          ? 'connecting'
          : connState === 'connected' && (iceState === 'disconnected' || iceState === 'failed' || iceState === 'closed')
            ? iceState
            : connState;
      list.push({ id, state: effectiveState });
    });
    return list;
  }

  async function replaceAudioTrack(newTrack) {
    const done = [];
    try {
      for (const [peerId, entry] of peers.entries()) {
        const pc = entry?.pc;
        if (!pc || pc.connectionState === 'closed') continue;
        const sender = pc.getSenders().find((s) => s.track && s.track.kind === 'audio');
        if (!sender) {
          console.warn('[단체 통화] 마이크 보내는 칸 없음 —', peerId);
          continue;
        }
        const old = sender.track;
        await sender.replaceTrack(newTrack);
        done.push({ sender, old });
      }
      return { ok: true, replaced: done.length };
    } catch (error) {
      for (const item of done) {
        await item.sender.replaceTrack(item.old).catch(() => {});
      }
      return { ok: false, error };
    }
  }

  async function applyVideoQuality(sender, level) {
    if (!sender || sender.track?.kind !== 'video') return;
    try {
      const p = sender.getParameters?.();
      if (!p?.encodings?.[0]) return;
      const enc = p.encodings[0];
      if (level === 0) {
        delete enc.maxBitrate;
        delete enc.maxFramerate;
        enc.scaleResolutionDownBy = 1;
      } else {
        const targetHeight = level === 1 ? 480 : 360;
        enc.maxBitrate = level === 1 ? 500000 : 350000;
        enc.maxFramerate = level === 1 ? 20 : 15;
        const th = sender.track.getSettings?.().height;
        enc.scaleResolutionDownBy = typeof th === 'number' && th > 0 ? Math.max(1, th / targetHeight) : 1;
      }
      await sender.setParameters(p);
    } catch (e) {
      console.warn('[단체 화상] 화질 설정 실패:', e);
    }
  }

  async function applyQualityToAll(level) {
    currentQualityLevel = level;
    const promises = [];
    peers.forEach((entry) => {
      if (entry.videoSender) promises.push(applyVideoQuality(entry.videoSender, level));
    });
    await Promise.allSettled(promises);
  }

  async function setLocalVideoTrack(track) {
    currentLocalVideoTrack = track || null;
    const promises = [];
    peers.forEach((peerEntry, peerId) => {
      const sender = peerEntry.videoSender;
      if (!sender) return;
      promises.push(
        sender.replaceTrack(currentLocalVideoTrack).catch((err) => {
          console.warn(`[단체 화상] replaceTrack 실패 — 상대: ${peerId?.slice?.(0, 8) || peerId}:`, err);
        })
      );
    });
    await Promise.all(promises);
    if (currentLocalVideoTrack) await applyQualityToAll(currentQualityLevel);
  }

  return {
    connectTo,
    handleOffer,
    handleAnswer,
    handleIce,
    handleReconnectPlease,
    closePeer,
    closeAll,
    peerIds,
    hasPeer,
    getPeerStates,
    replaceAudioTrack,
    setLocalVideoTrack,
    applyQualityToAll,
    reconnectPeer,
    getReconnectingPeerIds,
  };
}
